import {APP_BASE_HREF, CommonModule, DatePipe, DecimalPipe, registerLocaleData} from '@angular/common';
import locale_au from '@angular/common/locales/en-AU';
import locale_ca from '@angular/common/locales/en-CA';
import locale_fr from '@angular/common/locales/fr';
import locale_ja from '@angular/common/locales/ja';
import {ModuleWithProviders, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ActivationEnd, NavigationEnd, Router, RouterModule} from '@angular/router';
import {TranslateCompiler, TranslateModule, TranslateService} from '@ngx-translate/core';
import merge from 'lodash-es/merge';
import {TranslateMessageFormatCompiler} from 'ngx-translate-messageformat-compiler';
import {filter} from 'rxjs/operators';
import {AccordionHeaderComponent} from './accordion-header/accordion-header.component';
import {AddButtonComponent} from './add-button/add-button.component';
import {AnalyticsEventDirective} from './analytics-event.directive';
import {AnalyticsService} from './analytics.service';
import {ApiService} from './api.service';
import {AppStateService} from './app-state.service';
import {AuthRequiredGuard} from './auth-required.guard';
import {AuthGuard} from './auth.guard';
import {AuthService} from './auth.service';
import {AvatarComponent} from './avatar/avatar.component';
import {DropdownComponent} from './dropdown/dropdown.component';
import {FeedbackModalComponent} from './feedback-modal/feedback-modal.component';
import {FirstNamePipe} from './first-name.pipe';
import {SvgIconComponent} from './icon/svg-icon.component';
import {IdentificationService} from './identification.service';
import {IdentifyModalComponent} from './identify-modal/identify-modal.component';
import {InlineEditComponent} from './inline-edit/inline-edit.component';
import {LoaderComponent} from './loader/loader.component';
import {ModalService} from './modal.service';
import {ModalComponent} from './modal/modal.component';
import {NameFormatPipe} from './name-format.pipe';
import {NavigationService} from './navigation.service';
import {NormalizeUrlPipe} from './normalize-url.pipe';
import {OnboardingModalComponent} from './onboarding-modal/onboarding-modal.component';
import {PasswordGroupValidatorDirective} from './password-group-validator.directive';
import {PasswordStrengthIndicatorComponent} from './password-strength-indicator/password-strength-indicator.component';
import {PasswordsService} from './passwords.service';
import {RangePipe} from './range.pipe';
import {RedirectGuard} from './redirect.guard';
import {ReviewsHelpModalComponent} from './reviews-help-modal/reviews-help-modal.component';
import {SequencePipe} from './sequence.pipe';
import {StarsComponent} from './stars/stars.component';
import {StorageService} from './storage.service';
import {StylizedCheckboxComponent} from './stylized-checkbox/stylized-checkbox.component';
import {TabComponent} from './tab/tab.component';
import {TabsComponent} from './tabs/tabs.component';
import {TourStepTargetDirective} from './tour-step-target.directive';
import {TourStepComponent} from './tour-step/tour-step.component';
import {TourService} from './tour.service';
import {TruncatedTextComponent} from './truncated-text/truncated-text.component';
import {HorizontalBarGraphComponent} from '../client/diversity-insight/horizontal-bar-graph/horizontal-bar-graph.component';
import {GraphNg2chartsComponent} from './graph-ng2charts/graph-ng2charts.component';
import {ChartsModule} from 'ng2-charts';
import {YearAndQuarterPickerComponent} from './year-and-quarter-picker/year-and-quarter-picker.component';
import {NgxPopperModule} from 'ngx-popper';

const COMPONENTS = [
  LoaderComponent,
  ModalComponent,
  PasswordStrengthIndicatorComponent,
  StarsComponent,
  PasswordGroupValidatorDirective,
  TabsComponent,
  TabComponent,
  InlineEditComponent,
  AvatarComponent,
  DropdownComponent,
  SvgIconComponent,
  AddButtonComponent,
  StylizedCheckboxComponent,
  AnalyticsEventDirective,
  FeedbackModalComponent,
  OnboardingModalComponent,
  ReviewsHelpModalComponent,
  AccordionHeaderComponent,
  TruncatedTextComponent,
  IdentifyModalComponent,
  TourStepComponent,
  TourStepTargetDirective,
  HorizontalBarGraphComponent,
  GraphNg2chartsComponent
];

const PIPES = [
  NameFormatPipe,
  FirstNamePipe,
  RangePipe,
  NormalizeUrlPipe,
  SequencePipe,
];

export function BasePathFactory(): string {
  const base = document.head.querySelector('base');
  if (!base) return '/';
  const elem = document.createElement('a');
  elem.href = base.href;
  return elem.pathname.startsWith('/') ? elem.pathname : `/${elem.pathname}`; // IE workaround
}

/**
 * Provides shared modules and components.
 */
@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule.forRoot({
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler
      }
    }),
    RouterModule,
    ChartsModule,
    NgxPopperModule.forRoot({})
  ],
  declarations: [
    ...COMPONENTS,
    ...PIPES,
    GraphNg2chartsComponent,
    YearAndQuarterPickerComponent
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    RouterModule,
    ...COMPONENTS,
    ...PIPES,
    YearAndQuarterPickerComponent
  ],
  entryComponents: [
    StarsComponent
  ]
})
export class SharedModule {

  constructor(private Translate: TranslateService,
              private AppState: AppStateService,
              private Router: Router) {
    registerLocaleData(locale_fr);
    registerLocaleData(locale_ja);
    registerLocaleData(locale_au);
    registerLocaleData(locale_ca);

    const enTranslations = require('../../i18n/locale-en_US.json');
    const frTranslations = merge({}, enTranslations, require('../../i18n/locale-fr_FR.json'));
    const jaTranslations = merge({}, enTranslations, require('../../i18n/locale-ja_JP.json'));
    const gbTranslations = merge({}, enTranslations, require('../../i18n/locale-en_GB.json'));
    const caTranslations = merge({}, enTranslations, require('../../i18n/locale-en_CA.json'));
    const auTranslations = merge({}, gbTranslations, require('../../i18n/locale-en_AU.json'));
    Translate.setTranslation('en-AU', auTranslations);
    Translate.setTranslation('en-CA', caTranslations);
    Translate.setTranslation('en-GB', gbTranslations);
    Translate.setTranslation('en-US', enTranslations);
    Translate.setTranslation('fr', frTranslations);
    Translate.setTranslation('ja', jaTranslations);
    AppState.locale$.subscribe(locale => {
      if (['en-AU', 'en-CA', 'en-GB', 'en-US'].includes(locale)) {
        Translate.use(locale);
      } else {
        const lang = locale.split('-')[0];
        Translate.use(['fr', 'ja'].includes(lang) ? lang : 'en-US');
      }
    });

    Router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.AppState.loading$.next(false));

    Router.events
      .pipe(filter(e => e instanceof ActivationEnd))
      .subscribe(e => {
        if ((e as ActivationEnd).snapshot.queryParamMap.has('minimal')) {
          this.AppState.minimal$.next(true);
        }
      });
  }

  static forRoot(): ModuleWithProviders {
    return {
      ngModule: SharedModule,
      providers: [
        AnalyticsService,
        ApiService,
        AppStateService,
        AuthService,
        DatePipe,
        ModalService,
        PasswordsService,
        StorageService,
        DecimalPipe,
        RangePipe,
        IdentificationService,
        TourService,
        NavigationService,
        AuthGuard,
        AuthRequiredGuard,
        RedirectGuard,

        {provide: APP_BASE_HREF, useFactory: BasePathFactory}
      ]
    };
  }
}
